import Plotly, { Data, PlotData } from "plotly.js";
import { APIResponse } from "services/HttpService";
import { ReportOptions } from "models/component-print-models";
import { DashboardComponentPropsBase, IComponentTimed, IComponentTitled } from "components/pages/dashboard/components/interfaces";
import { ChartType } from "../dataset-window/models/types";
import { LineEditorLoadingTypes } from "../data-input-output-table/defaultConfigs";
import { IDateRange } from "../product-template-table/utils/interfaces";

export interface DatasetChartWrapperProps<ResT = any>
  extends DatasetChartProps,
    IComponentTimed,
    DashboardComponentPropsBase<ReportOptions> {
  datasetChart?: "pie" | "scatter" | "bar";
  uniqueKey: string;
  children?: (props: DatasetChartChildrenProps) => JSX.Element;
  promiseHandler: (res: APIResponse<ResT>) => PlotDataEx[];
  axesConfig?: (data: Partial<PlotDataEx>[]) => AxesConfig;
  chartConfig?: Partial<Plotly.Config>;
  maxDTicks?: number;
  dticks?: number;
  setDticks?: (value: number | undefined) => void;
  chartInitialized?: boolean;
  dateFrom?: Date;
  dateTo?: Date;
  dateAndRefreshButtonsVisible?: boolean;
  productItemsDateRange?: Array<string>;
  getMostRecent?: boolean;
}

export interface DatasetChartChildrenProps
  extends IComponentTimed,
    DashboardComponentPropsBase<ReportOptions> {
  width: number;
  height: number;
  axesConfig?: AxesConfig;
  data: Array<any>;
  chartConfig?: Partial<Plotly.Config>;
  chartDialogOpen?: boolean;
}

export interface DatasetChartProps
  extends IComponentTimed, IComponentTitled, 
    DashboardComponentPropsBase<ReportOptions>, IDateRange {
  identifiers?: Array<string>; // Dataset's identifiers
  height?: number | string; // Height in px
  width?: number | string;
  showDateSelection?: boolean; // Show date selection toolbar
  hideRefresh?: boolean; // Show refresh button when date selection is enabled
  loadConf?: LoadingConfig; // First load default config
  updateFrequency?: number;
  bins?: {
    start?: number | string;
    end?: number | string;
    size?: number | string;
  };
  loading?: boolean;

  dynamicHeight?: boolean;
  legend?: any;
  xaxisName?: string;
  chartConf?: ILineChartConf;

  datasets?: Array<DatasetSpecification>;
  toggleTracesGrouping?: boolean;
  timeAxisStepSeconds?: number;
  chartDialogOpen?: boolean;
  selectedChartType?: string;
  dateFrom?: Date;
  dateTo?: Date;
  setMinYAxis?: number;
  setMaxYAxis?: number;
  dateAndRefreshButtonsVisible?: boolean;
  productTemplateCreationDate?: Date;
  currentYAxisRange?: { min: number | undefined; max: number | undefined };
  annotations?: Annotation[];
  hideHeader?: boolean;
}
export interface DatasetSpecification {
  stringValueMapper?: number;
  overriddenTitle?: string;
  identifier: string;
  line?: ILineConfig;
  range?: Array<number>;
  color?: string;
}

export interface ILineConfig {
  backoff?: number | Array<number>;
  color?: string;
  dash?: string;
  shape?: string;
  simplify?: boolean;
  smoothing?: number;
  width?: number | null;
}

interface ILineChartConf {
  mode: "lines" | "lines+markers" | "text+lines+markers";
  marker: IMarkerConfig;
}

interface IMarkerConfig {
  size: number;
}

export interface DatasetBarChartProps
  extends DatasetChartProps,
    DashboardComponentPropsBase<ReportOptions> {
  dateFormat?: string;
}

export interface DatasetPieProps
  extends DatasetChartProps,
    DashboardComponentPropsBase<ReportOptions> {
  charts: Array<{
    aggregationType?: "average" | "sum";
    identifiers: Array<string>;
    chartName?: string;
  }>;
  chartsPerRow?: number;
  getMostRecent?: boolean;
  centerTextConfig?: {
    fontSize?: number;
    decimalPlaces?: number;
  };
}

export interface DatasetScatterProps extends DatasetChartProps, IDateRange {
  xAxisIdentifier: string;
  yAxisIdentifier: YAxisIdentifier[];
  colorAxisIdentifier?: string;
  sizeAxisIdentifier?: string;

  highlightCount?: number;
  trendLines: boolean;
  axisConfig?: {
    xRange?: Array<number>;
    yRange?: Array<number>;
  };

  imageConfig?: ImageConfig;

  annotationsConfig?: Array<AnnotationConfig>;
}

export interface YAxisIdentifierWithOverriddenTitle {
  identifier: string;
  overriddenTitle?: string;
}

export type YAxisIdentifier = YAxisIdentifierWithOverriddenTitle | string;

export type ImageConfig = {
  source: string;
  x: number;
  y: number;
  sizex: number;
  sizey: number;
  opacity: number;
};

export type AnnotationConfig = {
  x: number;
  y: number;
  text: string;

  showarrow?: boolean;
  xanchor?: "auto" | "left" | "center" | "right";
  yanchor?: "auto" | "top" | "middle" | "bottom";
  hovertext?: string;
  font?: {
    color?: string;
    size?: number;
  };
  opacity?: number;
  bordercolor?: string;
  borderpad?: number;
  borderwidth?: number;

  arrowcolor?: string;
  arrowside?: "start" | "end";
  arrowwidth?: number;
  bgcolor?: string;

  textangle?: string;
};

export type DatasetLoadingConf = {
  type?: LineEditorLoadingTypes.TIME;
  lead?: number; // Default 0
  lag: number; // Default 6
  leadUnit: string; // Default HOURS
  lagUnit: string; // Default HOURS
  alignUnit: string | null; // Default none/null
};

export class CharState {
  data: Array<Partial<PlotData>> = [];

  axisMap: Map<string, string> = new Map();

  axesConfig: any;
}

export type AxesConfig = {
  [key: string]: Plotly.LayoutAxis;
};

export interface PlotDataEx extends PlotData {
  identifier?: string;
  unit: string;
}

export interface IData {
  key: string;
  value: number;
}

export interface IDataSet {
  data: IData[];
  identifier: string;
  title: string;
  unit: string;
  decimalPlaces?: number;
  overriddenTitle?: string;
  stringValueMapper?: number;
  children?: IDataSet[];
  header?: string;
}

export interface ITrace {
  x: number[];
  name?: string;
  color?: string;
}

export interface IHistogram extends ITrace {
  histnorm: "percent";
  type: "histogram";
  opacity: number;
  loadConf?: DatasetLoadingConf; // First load default config
}

export interface IHorizontalBoxPlot extends ITrace {
  type: "box";
}

export interface IPlotChart {
  x: any;
  y: number[];
  type: "scatter";
  mode: "lines";
  name: string;
}

export interface DatasetStringValueMapperDTO {
  id?: number;
  title: string;
  company?: string;
  global: boolean;
  mappings: { [key: string]: string | number };
}

export interface IPIDControllerCommonProps {
  kp: string;
  ki: string;
  kd: string;
  delta: string;
  min: string;
  max: string;
  controlVariable: string;
  setPoint: string;
  manipulatedVariable: string;
  updateFrequency?: number;
  kall: string;
}

export interface IPIDControllerProps extends IPIDControllerCommonProps {
  loadConf?: DatasetLoadingConf;
  updateFrequency?: number;
}

export interface DataForExportLineAndScatter {
  identifier: string;
  name: string;
  type: string;
  mode: string;
  unit: string;
  showlegend: boolean;
  hovertemplate?: string;
  x?: string[];
  y?: number[];
}

export interface DataForExportBar {
  hovertemplate?: string;
  marker?: {
    color: string;
  };
  name: string;
  text: string[];
  type: string;
  unit: string;
  x?: string[];
  y?: number[];
}

export interface PieChartDataForExport {
  hole: number;
  hoverinfo: string;
  hovertemplate: string;
  labels: string[];
  values: number[];
  type: string;
  outsidetextfont?: {
    size: number;
  };
  text: string[];
  textinfo?: string;
  textfont?: {
    size: number;
  };
  marker?: {
    color: string;
  };
}

export interface DataForExportHistogram {
  customdata: number[];
  histnorm: string;
  hovertemplate: string;
  marker: {
    color: string;
  };
  name: string;
  showlegend: boolean;
  type: string;
  unit: string;
  x: number[];
  xbins?: any;
}

export interface IExportCsvDialogProps {
  data:
    | DataForExportLineAndScatter[]
    | DataForExportBar[]
    | PieChartDataForExport
    | DataForExportHistogram[]
    | Data[];
  dialog: boolean;
  setDialog: React.Dispatch<React.SetStateAction<boolean>>;
  chartType: ChartType;
}

export type LagLeadLoadingConfig = {
  type?: LineEditorLoadingTypes.TIME;
  lag: number;
  lead?: number;
  lagUnit: string;
  leadUnit: string;
  alignUnit: string | null;
};

export type ProductEntryLoadingConfig = {
  type: LineEditorLoadingTypes.PRODUCT_ENTRY;
  productTemplateId: string;
  startAttribute: string;
  endAttribute: string | null;
  refName?: string;
};

export interface WeatherComponentDatasetProps {
  isVisible?: boolean;
  dataset: {
    identifier: string;
    line: {
      color: string;
      width: number;
      dash: string;
    };
    overriddenTitle?: string;
  };
}

export interface DateSelectionProps {
  isVisible: boolean;
}

export interface WeatherMonitoringProps {
  timeInterval: TimeOptions;
  location?: string;
  temperature?: WeatherComponentDatasetProps;
  feelsLikeTemperature?: WeatherComponentDatasetProps;
  humidity?: WeatherComponentDatasetProps;
  windSpeed?: WeatherComponentDatasetProps;
  windDirection?: WeatherComponentDatasetProps;
  gusture?: WeatherComponentDatasetProps;
  precipitation?: WeatherComponentDatasetProps;
  dateSelection?: DateSelectionProps;
}

export interface WeatherMonitoringTableColumnRowData {
  time: Date;
  temperature: number;
  feelsLikeTemperature: number;
  humidity: number;
  windSpeed: number;
  windDirection: number;
  gusture: number;
  precipitation: number;
  [key: string]: number | Date;
}

export enum Visuals {
  NONE = "none",
  BLINK = "blink",
}

export interface StatusMonitoringIndicator {
  number: number;
  title: string;
  icon: string;
  color: string;
  sound: boolean;
  visual: Visuals;
}

export interface DatasetStatusMonitoringProps {
  identifiers: string[];
  updateFrequency: number;
  updateCheckTime: number;
  indicators: StatusMonitoringIndicator[];
  scale: number;
}

export type LoadingConfig = LagLeadLoadingConfig | ProductEntryLoadingConfig;

export interface Annotation {
  date: Date | null;
  text: string;
  dataset: string | null;
  y: number | null;
}